<script lang="ts" setup>
import type { StaticPage } from '~/types/StaticPage';
import type { MarketingTile } from '~/types/Catalog';
import type { StaticPageListItemDetails } from '~/types/StaticPageList';

const { selectedOffer } = storeToRefs(useCatalogStore());
const isShowMarketingTileModal = ref(false);
const tileContentSimplePage = ref<StaticPage | null>(null);
const tileContentBlogOrSpecialOfferPage = ref<StaticPageListItemDetails | null>(null);

function isNotAButton(tile: MarketingTile) {
  return (tile.page_slug && tile.description) || (!tile.page_slug && !tile.description);
}

const isLoading = ref<number | null>(null);

async function getPageContent(tile: MarketingTile) {
  if (isNotAButton(tile)) return;

  if (tile.page_slug) {
    isLoading.value = tile.id;

    switch (tile.page_type) {
      case 'simple_page':
        const staticPageStore = useStaticPageStore();
        tileContentSimplePage.value = await staticPageStore.getStaticPageBySlug(tile.page_slug);
        break;
      case 'special_offer':
        const specialOfferStore = useSpecialOffersStore();
        tileContentBlogOrSpecialOfferPage.value = await specialOfferStore.getSpecialOffersItemBySlug(tile.page_slug);
        break;
      case 'blog':
        const blogStore = useBlogStore();
        tileContentBlogOrSpecialOfferPage.value = await blogStore.getBlogItemBySlug(tile.page_slug);
        break;
      default:
        break;
    }

    isLoading.value = null;
  }

  if (tile.description) {
    tileContentSimplePage.value = {
      annotation: '',
      breadcrumbs: [],
      constructor: [{ type: 'text', text: tile.description }],
      header: tile.title,
      seoDescription: '',
      seoImage: null,
      seoText: '',
      seoTitle: '',
    };
  }

  isShowMarketingTileModal.value = true;
}
</script>

<template>
  <section
    v-if="selectedOffer.marketing_tiles.length"
    class="product-page__marketing-tiles"
  >
    <component
      :is="isNotAButton(tile) ? 'div' : 'button'"
      v-for="tile in selectedOffer.marketing_tiles"
      :key="tile.id"
      :style="{
        'border-color': tile.border_color ?? '#393d46',
        'background-color': tile.background_color ?? '#ffffff',
        color: tile.text_color ?? '#393d46',
      }"
      class="product-page__marketing-tiles-item"
      @click="getPageContent(tile)"
    >
      {{ tile.title }}
    </component>

    <template v-if="tileContentSimplePage">
      <BaseModal
        v-model="isShowMarketingTileModal"
        :title="tileContentSimplePage.header"
        @close="tileContentSimplePage = null"
      >
        <StaticPageConstructorBlocks
          :blocks="tileContentSimplePage.constructor"
          class="mb-6"
        />
      </BaseModal>
    </template>

    <template v-if="tileContentBlogOrSpecialOfferPage">
      <BaseModal
        v-model="isShowMarketingTileModal"
        @close="tileContentBlogOrSpecialOfferPage = null"
      >
        <StaticPage
          :item="tileContentBlogOrSpecialOfferPage"
          class="mb-6"
          no-breadcrumbs
        />
      </BaseModal>
    </template>
  </section>
</template>

<style lang="scss" scoped>
.product-page {
  &__marketing-tiles {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    font-size: $text-sm;
  }

  &__marketing-tiles-item {
    display: flex;
    justify-content: center;
    align-items: center;
    width: fit-content;
    padding: 8px 12px;
    border: 3px solid;
    border-radius: 8px;
  }
}
</style>
